import React, { useEffect, Suspense, lazy, createElement } from 'react';
import { SocialProof } from './components/SocialProof';
const PainPoints = lazy(() => import('./components/PainPoints').then(m => ({
  default: m.PainPoints
})));
const ProductShowcase = lazy(() => import('./components/ProductShowcase').then(m => ({
  default: m.ProductShowcase
})));
const WhatYouGet = lazy(() => import('./components/WhatYouGet').then(m => ({
  default: m.WhatYouGet
})));
const Bonuses = lazy(() => import('./components/Bonuses').then(m => ({
  default: m.Bonuses
})));
const Countdown = lazy(() => import('./components/Countdown').then(m => ({
  default: m.Countdown
})));
const Pricing = lazy(() => import('./components/Pricing').then(m => ({
  default: m.Pricing
})));
const ProBusiness = lazy(() => import('./components/ProBusiness').then(m => ({
  default: m.ProBusiness
})));
const Guarantee = lazy(() => import('./components/Guarantee').then(m => ({
  default: m.Guarantee
})));
const Testimonials = lazy(() => import('./components/Testimonials').then(m => ({
  default: m.Testimonials
})));
const FAQ = lazy(() => import('./components/FAQ').then(m => ({
  default: m.FAQ
})));
const DarkCTA = lazy(() => import('./components/DarkCTA').then(m => ({
  default: m.DarkCTA
})));
const FinalCTA = lazy(() => import('./components/FinalCTA').then(m => ({
  default: m.FinalCTA
})));
const Footer = lazy(() => import('./components/Footer').then(m => ({
  default: m.Footer
})));
const PurchaseNotifications = lazy(() => import('./components/PurchaseNotifications').then(m => ({
  default: m.PurchaseNotifications
})));
export function App() {
  // ── SEO & META TAGS ──
  useEffect(() => {
    document.title = 'Megapack Nutrición Resuelta — Consulta Resuelta™';
    document.documentElement.lang = 'es';
    const setMeta = (name: string, content: string, property?: boolean) => {
      const attr = property ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.content = content;
    };
    const description = 'Sistema clínico completo para nutricionistas. 6 Tomos, 30 planes listos para personalizar, recetario clínico con 100 recetas, protocolos de primera consulta y 6 bonos profesionales. Todo tu año de consultorio resuelto. Acceso inmediato.';
    // ── REEMPLAZÁ ESTA URL POR LA URL REAL DE TU DOMINIO ──
    const url = 'https://nutricionresuelta.com';
    // ── REEMPLAZÁ ESTA URL POR LA URL REAL DE TU MOCKUP ──
    const image = 'https://cdn.magicpatterns.com/uploads/9uMhQHg3RXXbKPqANDF9n5/hf_20260423_164447_343907b5-95aa-49ff-ba71-eeea9d0be6f6-Photoroom.webp';
    setMeta('description', description);
    setMeta('keywords', 'nutrición resuelta, megapack nutricionistas, planes alimentarios listos, primera consulta nutrición, sistema clínico nutrición, Argentina, consulta resuelta, nutricionistas argentina, recetario clínico');
    setMeta('robots', 'index, follow');
    setMeta('author', 'Consulta Resuelta™');
    setMeta('viewport', 'width=device-width, initial-scale=1');
    if (!document.querySelector('link[rel="canonical"]')) {
      const canonical = document.createElement('link');
      canonical.rel = 'canonical';
      canonical.href = url;
      document.head.appendChild(canonical);
    }
    // ── OPEN GRAPH ──
    setMeta('og:title', 'Megapack Nutrición Resuelta — Todo tu año de consultorio resuelto', true);
    setMeta('og:description', description, true);
    setMeta('og:image', image, true);
    setMeta('og:url', url, true);
    setMeta('og:type', 'product', true);
    setMeta('og:locale', 'es_AR', true);
    setMeta('og:site_name', 'Consulta Resuelta™', true);
    // ── TWITTER CARD ──
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', 'Megapack Nutrición Resuelta — Consulta Resuelta™');
    setMeta('twitter:description', description);
    setMeta('twitter:image', image);
    // ── SCHEMA: PRODUCT ──
    if (!document.querySelector('script[data-ld="product"]')) {
      const ld = document.createElement('script');
      ld.type = 'application/ld+json';
      ld.setAttribute('data-ld', 'product');
      ld.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: 'Megapack Nutrición Resuelta — Sistema Clínico Completo™',
        description: description,
        image: image,
        brand: {
          '@type': 'Brand',
          name: 'Consulta Resuelta™'
        },
        offers: {
          '@type': 'AggregateOffer',
          lowPrice: '19990',
          highPrice: '27990',
          priceCurrency: 'ARS',
          availability: 'https://schema.org/InStock',
          offerCount: 2
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.9',
          reviewCount: '634'
        }
      });
      document.head.appendChild(ld);
    }
    // ── SCHEMA: ORGANIZATION ──
    if (!document.querySelector('script[data-ld="org"]')) {
      const orgLd = document.createElement('script');
      orgLd.type = 'application/ld+json';
      orgLd.setAttribute('data-ld', 'org');
      orgLd.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'Consulta Resuelta™',
        url: url,
        logo: image,
        // ── REEMPLAZÁ POR TU EMAIL REAL ──
        email: 'hola@consultaresuelta.com',
        sameAs: [
        // ── REEMPLAZÁ POR TU INSTAGRAM REAL ──
        'https://www.instagram.com/consultaresuelta']
      });
      document.head.appendChild(orgLd);
    }
    // ── SCHEMA: FAQ ──
    if (!document.querySelector('script[data-ld="faq"]')) {
      const faqLd = document.createElement('script');
      faqLd.type = 'application/ld+json';
      faqLd.setAttribute('data-ld', 'faq');
      faqLd.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [{
          '@type': 'Question',
          name: '¿Qué incluye el Megapack Nutrición Resuelta?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '6 Tomos completos con sistema de primera consulta, 30 planes listos para personalizar, recetario clínico con 100 recetas, material educativo para entregar, sistema de seguimiento y 6 bonos profesionales incluyendo 15 casos clínicos resueltos.'
          }
        }, {
          '@type': 'Question',
          name: '¿Cómo recibo el material?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Todo es 100% digital en formato PDF. Recibís acceso inmediato en tu email dentro de los 5 minutos posteriores a la compra.'
          }
        }, {
          '@type': 'Question',
          name: '¿Tiene garantía?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Sí, ofrecemos garantía de 7 días sin preguntas. Si no te sirve, te devolvemos el 100% de tu dinero.'
          }
        }, {
          '@type': 'Question',
          name: '¿Está adaptado a Argentina?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Sí, 100%. Los planes, recetas y protocolos están adaptados al contexto clínico, cultural y alimentario argentino.'
          }
        }, {
          '@type': 'Question',
          name: '¿Para qué tipo de nutricionista es este sistema?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Para nutricionistas que atienden en consultorio presencial u online, tanto recién graduados como con experiencia, que quieren ahorrar tiempo, entregar material profesional y aumentar la adherencia de sus pacientes.'
          }
        }]
      });
      document.head.appendChild(faqLd);
    }
  }, []);
  // ── FACEBOOK PIXEL ──
  useEffect(() => {
    const loadFbPixel = () => {
      if (typeof window !== 'undefined' && !(window as any).fbq) {
        const f = window as any;
        const n = f.fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = '2.0';
        n.queue = [];
        (window as any).fbq('init', 'TU_PIXEL_ID_AQUI');
        (window as any).fbq('track', 'PageView');
        const t = document.createElement('script');
        t.async = true;
        t.src = 'https://connect.facebook.net/en_US/fbevents.js';
        const s = document.getElementsByTagName('script')[0];
        s?.parentNode?.insertBefore(t, s);
      }
    };
    if ('requestIdleCallback' in window) {
      ;
      (window as any).requestIdleCallback(loadFbPixel, {
        timeout: 3000
      });
    } else {
      setTimeout(loadFbPixel, 2000);
    }
  }, []);
  return <>
      {/* ── FACEBOOK PIXEL NOSCRIPT FALLBACK ── */}
      <noscript data-id="element-0">
        <img height="1" width="1" style={{
        display: 'none'
      }}
      // ── REEMPLAZÁ POR TU PIXEL ID REAL ──
      src="https://www.facebook.com/tr?id=TU_PIXEL_ID_AQUI&ev=PageView&noscript=1" alt="" data-id="element-1" />
      </noscript>

      {/* ── HEADER / HERO ── */}
      <header className="relative flex w-full flex-col items-center justify-center text-center text-gray-900 text-base leading-6 px-5 sm:px-6 py-12 sm:py-16" role="banner" style={{
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      backgroundImage: 'linear-gradient(135deg, rgb(240,255,244) 0%, rgb(220,252,231) 30%, rgb(240,255,244) 60%, rgb(248,250,252) 100%)',
      backgroundColor: 'rgb(255, 255, 255)'
    }} data-id="element-2">
        {/* ── 1. BADGE ── */}
        <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-green-600/30 bg-white/80 px-4 py-1.5 shadow-sm" data-id="element-3">
          <span className="text-xs sm:text-sm font-bold text-green-700 tracking-wide uppercase" data-id="element-4">
            🥑 Sistema Clínico Profesional · Nutricionistas Argentina
          </span>
        </div>

        {/* ── 2. H1 ── */}
        <h1 className="sm:text-5xl md:text-[72px] sm:leading-snug md:leading-[88px] font-black max-w-4xl text-gray-900 m-0 mb-2 text-[28px] leading-10 tracking-tight" data-id="element-5">
          Dejá de perder horas
          <br data-id="element-6" />
          planificando desde cero.
          <br data-id="element-7" />
          <span className="text-green-700 mt-1 inline-block" data-id="element-8">
            Tu consultorio, resuelto.
          </span>
        </h1>

        {/* ── 3. MOCKUP ── */}
        <div className="my-6 sm:my-8 w-full max-w-[320px] sm:max-w-[380px] aspect-square relative" style={{
        perspective: '900px'
      }} data-id="element-9">
          {/* Green glow behind mockup */}
          <div className="absolute inset-4 rounded-full z-0 pointer-events-none" style={{
          background: 'radial-gradient(circle, rgba(34,197,94,0.30) 0%, rgba(34,197,94,0.10) 50%, transparent 70%)'
        }} data-id="element-10" />
          {/* 3D tilted wrapper */}
          <div className="relative z-10 w-full h-full" style={{
          transform: 'rotateY(-5deg) rotateX(3deg)',
          transformStyle: 'preserve-3d'
        }} data-id="element-11">
            <img src="https://cdn.magicpatterns.com/uploads/9uMhQHg3RXXbKPqANDF9n5/hf_20260423_164447_343907b5-95aa-49ff-ba71-eeea9d0be6f6-Photoroom.webp" alt="Megapack Nutrición Resuelta — Sistema Clínico Completo para Nutricionistas" width="400" height="400" fetchPriority="high" decoding="sync" className="block max-w-full w-full h-auto object-contain" style={{
            filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.25)) drop-shadow(0 8px 16px rgba(34,197,94,0.25))'
          }} data-id="element-12" />
          </div>
        </div>

        {/* ── 4. SUBTÍTULO ── */}
        <p className="max-w-xl text-base sm:text-lg font-semibold text-gray-700 leading-7 m-0 mb-4" data-id="element-13">
          El sistema que los nutricionistas más organizados de Argentina usan
          para llegar al consultorio preparados, entregar material profesional y{' '}
          <span className="text-gray-900 font-black" data-id="element-14">
            cobrar lo que realmente vale su trabajo.
          </span>
        </p>

        {/* ── 5. TAGLINE MECANISMO ── */}
        <p className="max-w-xl text-lg sm:text-xl font-black text-green-700 m-0 mb-6" data-id="element-15">
          "30 planes listos. 100 recetas clínicas.
          <br className="hidden sm:block" data-id="element-16" />
          Sin improvisar más, sin agotarte más."
        </p>

        {/* ── 6. TAGS ── */}
        <div className="mb-7 flex max-w-lg flex-wrap items-center justify-center gap-2" data-id="element-17">
          {['📄 100% PDF', '⚡ Acceso Inmediato', '🇦🇷 Argentina', '🥑 Nutrición Clínica', '✅ Sistema Listo', '💼 6 Tomos + 6 Bonos'].map(tag => <span key={tag} className="whitespace-nowrap bg-white/80 text-xs sm:text-sm font-semibold text-gray-900 leading-5 border border-green-600/30 rounded-full px-3 py-1 shadow-sm" data-id="element-18">
              {tag}
            </span>)}
        </div>

        {/* ── 7. CTA BUTTON ── */}
        <a href="#pricing" onClick={() => {
        if (typeof window !== 'undefined' && (window as any).fbq) {
          ;
          (window as any).fbq('track', 'Lead', {
            content_name: 'Hero CTA Click',
            content_category: 'Nutrición Resuelta'
          });
        }
      }} className="block w-full max-w-sm bg-green-700 text-white text-lg sm:text-xl font-black rounded-xl px-6 sm:px-8 py-4 mb-4 no-underline text-center shadow-lg hover:opacity-90 transition-opacity" data-id="element-19">
          🥑 → QUIERO MI MEGAPACK AHORA
        </a>

        {/* ── 8. TRUST BADGES ── */}
        <div className="flex flex-row flex-wrap items-center justify-center gap-3 sm:gap-4 m-0" data-id="element-20">
          <span className="inline-flex items-center gap-1.5 bg-white/80 border border-gray-200 rounded-full px-3 py-1.5 text-xs sm:text-sm text-gray-600 shadow-sm" data-id="element-21">
            🛡️ Garantía 7 días
          </span>
          <span className="inline-flex items-center gap-1.5 bg-white/80 border border-gray-200 rounded-full px-3 py-1.5 text-xs sm:text-sm text-gray-600 shadow-sm" data-id="element-22">
            🔒 Pago seguro
          </span>
          <span className="inline-flex items-center gap-1.5 bg-white/80 border border-gray-200 rounded-full px-3 py-1.5 text-xs sm:text-sm text-gray-600 shadow-sm" data-id="element-23">
            💳 Mercado Pago | Tarjeta | Transferencia
          </span>
        </div>
      </header>

      {/* ── MAIN SECTIONS ── */}
      <main data-id="element-24">
        <SocialProof data-id="element-25" />
        <Suspense fallback={null} data-id="element-26">
          <PainPoints data-id="element-27" />
          <ProductShowcase data-id="element-28" />
        </Suspense>
        <Suspense fallback={null} data-id="element-29">
          <WhatYouGet data-id="element-30" />
          <Bonuses data-id="element-31" />
          <Countdown data-id="element-32" />
          <Pricing data-id="element-33" />
          <ProBusiness data-id="element-34" />
          <Guarantee data-id="element-35" />
          <Testimonials data-id="element-36" />
          <FAQ data-id="element-37" />
          <DarkCTA data-id="element-38" />
          <FinalCTA data-id="element-39" />
        </Suspense>
      </main>

      {/* ── FOOTER ── */}
      <Suspense fallback={null} data-id="element-40">
        <Footer data-id="element-41" />
        <PurchaseNotifications data-id="element-42" />
      </Suspense>
    </>;
}